"use client"

import type React from "react"
import { useRef } from "react"

import { Wand2, Loader2, AlertTriangle, Lock, Mail } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SocialLoginButtons } from "@/components/auth/SocialLoginButtons"
import {
  CarePlanDeliverables,
  type CarePlanDeliverable,
} from "./care-plan-deliverables"
import type { EmailSuggestion } from "@/lib/email-typo-detection"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { analytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import {
  getMonthlyTasks,
  getCurrentMonthIndex,
  getMonthName,
  getTotalTaskCount,
  normalizeGrassKey,
} from "@/data/guide-monthly-tasks"

interface MonthPreviewTask {
  task: string
  priority: 'high' | 'medium' | 'low'
}

interface MonthPreview {
  month: string
  visible: MonthPreviewTask[]
  peek: MonthPreviewTask | null
  remaining: number
  totalTasks: number
}

// Show all current-month tasks (typically 3-4) with priority dots, then one
// blurred peek from next month. Audit feedback was that walling 12 months
// behind 1 visible task feels stingy — Strava/Calm pattern is to give away
// the current period's depth so the wall feels like unlocking the future,
// not gating the present.
function monthPreviewFor(grassType: string | undefined): MonthPreview | null {
  if (!grassType) return null
  const key = normalizeGrassKey(grassType) ?? grassType
  const monthIndex = getCurrentMonthIndex()
  const tasks = getMonthlyTasks(key, monthIndex)
  if (!tasks.length) return null
  const visible = tasks.map((t) => ({ task: t.task, priority: t.priority }))
  // Peek from the next month — proves the plan extends without spoiling it.
  // December wraps to January via mod 12.
  const nextTasks = getMonthlyTasks(key, (monthIndex + 1) % 12)
  const peek = nextTasks.length
    ? { task: nextTasks[0].task, priority: nextTasks[0].priority }
    : null
  const totalTasks = getTotalTaskCount(key)
  // Subtract everything we showed: visible current-month + the peek row.
  const shown = visible.length + (peek ? 1 : 0)
  const remaining = Math.max(0, totalTasks - shown)
  return {
    month: getMonthName(monthIndex),
    visible,
    peek,
    remaining,
    totalTasks,
  }
}

const PRIORITY_DOT: Record<'high' | 'medium' | 'low', string> = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-gray-400',
}

interface EmailGateProps {
  variant: 'gate' | 'cta'
  email: string
  emailSuggestion: EmailSuggestion | null
  isSubmitting: boolean
  grassType?: string
  region?: string
  /** The user's own uploaded grass photo, shown as a recognition thumbnail in
   *  the cta header. Faces/objects process orders of magnitude faster than text
   *  (amber-alert.md), and it's literally their lawn — the strongest trust cue. */
  uploadedImage?: string
  timelineRedirectUrl?: string
  /** Scan join keys, stamped on the start and OAuth events. */
  analysisId?: string | null
  funnelId?: string | null
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onEmailKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onSubmit: () => void
  onAcceptSuggestion: () => void
  onDismissSuggestion: () => void
  className?: string
}

export function EmailGate({
  variant,
  email,
  emailSuggestion,
  isSubmitting,
  grassType,
  region,
  uploadedImage,
  timelineRedirectUrl,
  analysisId,
  funnelId,
  onEmailChange,
  onEmailKeyPress,
  onSubmit,
  onAcceptSuggestion,
  onDismissSuggestion,
  className
}: EmailGateProps) {
  // Impression: the parent fires `email_captured` with source `grass_id_gate`
  // (use-homepage-analysis.ts), so the impression uses that same source to join
  // the funnel. One ref, attached to whichever variant renders.
  const impressionRef = useImpressionEvent<HTMLDivElement>('grass_id_gate', { variant })
  const startTrackedRef = useRef(false)
  const analysisContext = { analysisId, funnelId }

  // Same source on the OAuth lane, so a signup that starts here carries a
  // capture_source instead of landing in the unattributed bucket.
  const handleBeforeRedirect = () => {
    stashEmailCaptureAttribution('grass_id_gate')
  }

  // First focus of the email field, once per mount. Same source as the
  // impression and the capture so the three steps join.
  const handleEmailFocus = () => {
    if (startTrackedRef.current) return
    startTrackedRef.current = true
    analytics.emailCaptureStarted({
      source: 'grass_id_gate',
      variant,
      analysis_id: analysisId ?? undefined,
      funnel_id: funnelId ?? undefined,
    })
  }

  // Email input — shared by gate and CTA variants.
  const EmailInput = (
    <div>
      <Input
        id={variant === 'gate' ? 'gate-email' : 'cta-email'}
        type="email"
        name="email"
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={email}
        onChange={onEmailChange}
        onKeyDown={onEmailKeyPress}
        onFocus={handleEmailFocus}
        placeholder={variant === 'gate' ? "Enter your email address" : "Enter your email"}
        className={cn(
          "w-full border-2 border-green-200 rounded-xl focus:border-green-500 focus:ring-0 focus:outline-hidden transition-colors duration-200",
          variant === 'gate' ? "px-4 py-4 text-lg" : "px-4 py-3 md:py-4 text-base md:text-lg"
        )}
        autoComplete="email"
        style={{ boxShadow: 'none' }}
      />

      {/* Email typo suggestion */}
      {emailSuggestion && (
        <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="text-sm text-amber-800">
              Did you mean <strong>{emailSuggestion.correctedDomain}</strong>?
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onAcceptSuggestion}
              className="text-sm font-medium text-green-700 hover:text-green-800 px-2 py-1 rounded hover:bg-green-50 transition-colors"
            >
              Yes, fix it
            </button>
            <button
              type="button"
              onClick={onDismissSuggestion}
              className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
            >
              No
            </button>
          </div>
        </div>
      )}
    </div>
  )

  // Gate variant — preserved for callers that still want the email-first flow.
  if (variant === 'gate') {
    return (
      <div ref={impressionRef} className={cn("bg-linear-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200", className)}>
        <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
          Get Your Free Care Plan
        </h3>
        <p className="text-gray-600 mb-4 text-center">
          Get your personalized 12-month care schedule
        </p>

        <div className="space-y-4">
          {/* OAuth - Primary CTA */}
          {timelineRedirectUrl && (
            <SocialLoginButtons
              placement="result_email_gate"
              analysisContext={analysisContext}
              redirectTo={timelineRedirectUrl}
              onBeforeRedirect={handleBeforeRedirect}
            />
          )}

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">or continue with email</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Email - Secondary fallback */}
          {EmailInput}

          <Button
            onClick={onSubmit}
            disabled={isSubmitting}
            className="w-full bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed min-h-[56px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Wand2 className="w-5 h-5 mr-2" />
                Send My Care Plan
              </>
            )}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            Free • No spam • No credit card
          </p>
        </div>
      </div>
    )
  }

  // CTA variant — the post-ID signup card. Previously OAuth-only with a
  // collapsed "just email me" link; PostHog showed 12 OAuth signups vs 11
  // email captures over 30d (conversion-equal but email was rarely found
  // because it was buried behind a click). Restoring inline OAuth + divider
  // + email keeps the audit's "OAuth visually primary" intent — the social
  // buttons stay at the top with proof — but stops starving the email lane.
  const deliverables = carePlanDeliverablesFor(grassType)
  const preview = monthPreviewFor(grassType)
  const planLine = grassType
    ? region
      ? `Built for ${grassType} in the ${region}.`
      : `Built for ${grassType}.`
    : "Built for your lawn."

  return (
    <div
      ref={impressionRef}
      className={cn(
        "relative w-full max-w-full min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 md:p-8 shadow-sm",
        className,
      )}
    >
      <div className="mb-5 flex items-start gap-3">
        {uploadedImage && (
          <img
            src={uploadedImage}
            alt="Your grass photo"
            className="h-12 w-12 flex-shrink-0 rounded-lg border border-gray-200 object-cover"
          />
        )}
        <div className="min-w-0">
          <div className="text-[11px] md:text-xs font-bold uppercase tracking-[0.06em] text-green-700 mb-1.5">
            Your free care plan
          </div>
          <div className="text-xl md:text-2xl font-extrabold tracking-tight text-gray-900 leading-tight">
            {planLine}
          </div>
        </div>
      </div>

      {/* Deliverables — concrete numbers do the trust work that icons can't */}
      <CarePlanDeliverables items={deliverables} className="mb-4" />

      {/* Current-month preview — show the full real list of tasks for this
          month (typically 3-4 items) so the wall reads as "unlock the rest
          of the year", not "unlock the plan itself". Priority dots match
          the in-app task styling so the unlocked version feels continuous. */}
      {preview && (
        <div className="mb-5 rounded-xl border border-green-200 bg-green-50/60 px-4 py-3">
          <div className="flex items-baseline justify-between gap-2 mb-2">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-green-700">
              This month · {preview.month}
            </div>
            {preview.totalTasks > 0 && (
              <div className="text-[10.5px] font-medium text-green-700/70 whitespace-nowrap">
                {preview.visible.length} of {preview.totalTasks}
              </div>
            )}
          </div>

          <ul className="space-y-1.5">
            {preview.visible.map((t, i) => (
              <li key={i} className="flex items-start gap-2 min-w-0">
                <span
                  className={cn(
                    "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                    PRIORITY_DOT[t.priority],
                  )}
                  aria-hidden="true"
                />
                <span className="flex-1 min-w-0 text-[13.5px] text-gray-900 leading-snug">
                  {t.task}
                </span>
              </li>
            ))}
          </ul>

          {/* Next-month peek — Strava/Calm pattern: show the next task styled
              like a real task line so the wall feels like unlocking content.
              select-none + pointer-events-none keeps it unreadable/inert. */}
          {preview.peek && (
            <div
              aria-hidden="true"
              className="mt-2.5 pt-2.5 border-t border-green-200/70 flex items-center gap-2 min-w-0 select-none pointer-events-none"
            >
              <span className={cn("h-1.5 w-1.5 rounded-full flex-shrink-0", PRIORITY_DOT[preview.peek.priority])} />
              <span className="flex-1 min-w-0 text-[13px] text-gray-700 leading-snug truncate blur-[3px] opacity-70">
                {preview.peek.task}
              </span>
            </div>
          )}

          {preview.remaining > 0 && (
            <div className="mt-2 flex items-start gap-1.5 text-[11.5px] font-semibold text-green-700 min-w-0">
              <Lock className="h-3 w-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
              <span className="min-w-0">+ {preview.remaining} more tasks across your 12-month plan</span>
            </div>
          )}
        </div>
      )}

      {/* Sign-in prompt — "View your 12-month plan" matches what the user
          actually wants to do (see the plan), not a save action that implies
          they already have it. */}
      <p className="text-sm font-semibold text-gray-900 text-center mb-2">
        View your 12-month plan
      </p>

      <p className="mb-3 text-center text-xs text-gray-600">
        Keep this plan on all your devices, free.
      </p>

      {/* OAuth — side-by-side reads as one decision instead of a stack of options */}
      {timelineRedirectUrl && (
        <SocialLoginButtons
          placement="result_email_gate_cta"
          analysisContext={analysisContext}
          redirectTo={timelineRedirectUrl}
          compact
          onBeforeRedirect={handleBeforeRedirect}
        />
      )}

      {/* Divider + inline email form. Restoring the always-visible email lane
          (vs the prior deferred-reveal "Just email me the plan instead" link)
          because the previous design starved the email path — 30-day capture
          counts were 11 email / 12 OAuth, but the underlined-link affordance
          was reading as a dismiss rather than a CTA. The divider preserves
          OAuth as the visually primary path. */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-[11px] text-gray-500 uppercase tracking-wide">or email me the plan</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* Specificity at the point of action: name the exact deliverable (the
          grass-specific 12-month plan, which the care_plan context actually
          sends) so the email lane reads as the same value as the OAuth path. */}
      <p className="mt-3 text-xs text-gray-600 text-center text-pretty">
        We&apos;ll email your 12-month {grassType ? `${grassType} ` : ""}care plan, built for your grass and season.
      </p>

      <div className="mt-2 space-y-2.5">
        {EmailInput}
        <Button
          onClick={onSubmit}
          disabled={isSubmitting}
          className="w-full bg-green-700 hover:bg-green-800 text-white py-3 px-4 rounded-xl font-semibold text-sm shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4 mr-2" />
              Send My Care Plan
            </>
          )}
        </Button>
        <p className="text-[10.5px] text-gray-400 text-center">
          Free · No credit card
        </p>
      </div>
    </div>
  )
}

// Trimmed to 2 tiles: mowing + watering are the two facts every lawn owner
// scans for first. Fertilizer cadence and 12-month-plan info are now carried
// by the next-task peek + lock line below, so the grid doesn't need to
// duplicate that weight.
const DEFAULT_DELIVERABLES: CarePlanDeliverable[] = [
  { kind: 'mower', title: 'Mowing height', value: 'Tuned to your grass' },
  { kind: 'water', title: 'Watering', value: 'Weekly inches' },
]

function carePlanDeliverablesFor(grassType: string | undefined): CarePlanDeliverable[] {
  if (!grassType) return DEFAULT_DELIVERABLES

  const profile = GRASS_DELIVERABLES[grassType]
  if (!profile) return DEFAULT_DELIVERABLES

  return [
    { kind: 'mower', title: 'Mowing height', value: profile.mow },
    { kind: 'water', title: 'Watering', value: profile.water },
  ]
}

// Hand-tuned pulls from data/grass-characteristics.ts — formatted for a tight
// 2x2 grid (no inches symbol overflow, en-dash ranges, "/ wk" for cadence).
const GRASS_DELIVERABLES: Record<string, { mow: string; water: string; fertilizer: string }> = {
  'Kentucky Bluegrass': { mow: '2.0–3.5"', water: '1.0–1.5" / wk', fertilizer: '4 windows / yr' },
  'Perennial Ryegrass': { mow: '1.5–2.5"', water: '1.0–1.5" / wk', fertilizer: '3 windows / yr' },
  'Tall Fescue': { mow: '3.0–4.0"', water: '1.0–1.25" / wk', fertilizer: '3 windows / yr' },
  'Fine Fescue': { mow: '2.5–3.5"', water: '0.5–1.0" / wk', fertilizer: '2 windows / yr' },
  'Bermudagrass': { mow: '0.5–1.5"', water: '1.0–1.25" / wk', fertilizer: '4 windows / yr' },
  'Zoysiagrass': { mow: '1.0–2.0"', water: '0.75–1.0" / wk', fertilizer: '3 windows / yr' },
  'Centipede': { mow: '1.0–2.0"', water: '0.5–1.0" / wk', fertilizer: '2 windows / yr' },
  'Bahiagrass': { mow: '2.5–3.5"', water: '0.5–1.0" / wk', fertilizer: '2 windows / yr' },
  'Buffalograss': { mow: '2.0–3.0"', water: '0.25–0.5" / wk', fertilizer: '1 window / yr' },
  'St. Augustine': { mow: '2.5–4.0"', water: '0.75–1.0" / wk', fertilizer: '3 windows / yr' },
}
